import {
    buildDriverSteps,
    startDriverTour,
} from 'js/onboarding/driverTour';
import {
    buildScopedTourStorageKey,
    readTourState,
    writeTourState,
} from 'js/onboarding/tourState';

export const BILLING_SUBSCRIPTION_TOUR_ID = 'billing';
export const BILLING_SUBSCRIPTION_TOUR_VERSION = 'v1';

const DEFAULT_BILLING_TOUR_STATE = Object.freeze({
    status: 'not_started',
});

export function getBillingSubscriptionTourStorageKey({ user = '' } = {}) {
    return buildScopedTourStorageKey({
        tourId: BILLING_SUBSCRIPTION_TOUR_ID,
        version: BILLING_SUBSCRIPTION_TOUR_VERSION,
        user,
    });
}

export function getBillingSubscriptionTourState(
    context = {},
    storage = window.localStorage
) {
    return readTourState(
        getBillingSubscriptionTourStorageKey(context),
        storage,
        DEFAULT_BILLING_TOUR_STATE
    );
}

export function saveBillingSubscriptionTourState(
    context = {},
    nextState = {},
    storage = window.localStorage
) {
    return writeTourState(
        getBillingSubscriptionTourStorageKey(context),
        nextState,
        storage,
        DEFAULT_BILLING_TOUR_STATE
    );
}

export function markBillingSubscriptionTourCompleted(
    context = {},
    storage = window.localStorage
) {
    return saveBillingSubscriptionTourState(
        context,
        { status: 'completed' },
        storage
    );
}

export function markBillingSubscriptionTourDismissed(
    context = {},
    storage = window.localStorage
) {
    return saveBillingSubscriptionTourState(
        context,
        { status: 'dismissed' },
        storage
    );
}

export function shouldStartBillingSubscriptionTour({
    isAuthenticated = false,
    state = DEFAULT_BILLING_TOUR_STATE,
} = {}) {
    if (!isAuthenticated) return false;
    return !['completed', 'dismissed'].includes(state?.status);
}

export function buildBillingSubscriptionTourSteps() {
    return [
        {
            title: 'Subscription Overview',
            target: '[data-billing-tour-overview]',
            content: 'Start here to review the account summary, plan status, and billing controls.',
        },
        {
            title: 'Current Plan',
            target: '[data-billing-tour-current-plan]',
            content: 'This card shows the active plan, pricing, and the main account action for changes.',
        },
        {
            title: 'Dataset Usage',
            target: '[data-billing-tour-dataset-usage]',
            content: 'Track how many datasets are in use against your allowance.',
        },
        {
            title: 'Storage Usage',
            target: '[data-billing-tour-storage-usage]',
            content: 'Check current storage consumption and remaining capacity before a large upload.',
        },
        {
            title: 'Seat Usage',
            target: '[data-billing-tour-seat-usage]',
            content: 'See how many user seats are assigned to the workspace.',
        },
        {
            title: 'Billing History',
            target: '[data-billing-tour-history]',
            content: 'Review invoices, payments, and the account history from the billing section.',
        },
        {
            title: 'Upgrade / Contact Sales',
            target: '[data-billing-tour-upgrade]',
            content: 'Use this area when you need a larger plan or want to contact the team for a custom setup.',
        },
    ];
}

export async function startBillingSubscriptionTour({
    context = {},
    storage = window.localStorage,
    createDriver,
} = {}) {
    const state = getBillingSubscriptionTourState(context, storage);
    if (!shouldStartBillingSubscriptionTour({ isAuthenticated: Boolean(context?.user), state })) {
        return { started: false, state };
    }

    const steps = await buildDriverSteps(buildBillingSubscriptionTourSteps());
    if (!steps.length) {
        return { started: false, state };
    }

    const tour = startDriverTour({
        steps,
        createDriver,
        onCompleted: () => markBillingSubscriptionTourCompleted(context, storage),
        onDismissed: () => markBillingSubscriptionTourDismissed(context, storage),
    });

    return { started: Boolean(tour), state, tour };
}
